import React from 'react'
import { useT } from '../i18n'

export type CurrentContextField = {
  label: string
  value: string
  placeholder?: string
}

export type CurrentContextBarProps = {
  species?: {
    value: string
    label?: string
    placeholder?: string
  }
  fields: CurrentContextField[]
  style?: React.CSSProperties
}

const containerStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 20,
  width: '100%',
  padding: '8px 12px',
  marginBottom: '12px',
  background: '#e7f3ff',
  border: '1px solid #bfdbfe',
  borderRadius: '10px',
  color: '#1d4ed8',
  boxSizing: 'border-box'
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: '8px 10px',
  lineHeight: 1.3
}

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: '#1e3a8a'
}

const valueStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#1d4ed8',
  maxWidth: '100%',
  overflow: 'hidden',
  wordBreak: 'break-word',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as const
}

const fieldStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: 6,
  minWidth: 0
}

const separatorStyle: React.CSSProperties = {
  fontSize: 12,
  opacity: 0.7
}

export default function CurrentContextBar({ species, fields, style }: CurrentContextBarProps) {
  const t = useT()
  const effectiveFields = (species
    ? ([
        {
          label: species.label ?? t('common.species'),
          value: species.value,
          placeholder: species.placeholder
        },
        ...fields
      ] satisfies CurrentContextField[])
    : fields)

  if (!effectiveFields.length) return null

  return (
    <div style={style ? { ...containerStyle, ...style } : containerStyle} aria-label="Current selection context">
      <div style={rowStyle}>
        {effectiveFields.map((field, index) => {
          const displayValue = field.value.trim() || field.placeholder || '—'
          return (
            <React.Fragment key={`${field.label}-${index}`}>
              <div style={fieldStyle}>
                <span style={labelStyle}>{field.label}:</span>
                <span style={valueStyle} title={displayValue}>
                  {displayValue}
                </span>
              </div>
              {index < effectiveFields.length - 1 ? <span style={separatorStyle}>·</span> : null}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}
